import { useState } from "react";
import { PanelShell, StatChip, Badge, tokens } from "../index";
import { brandConsistencyScore, requiresReviewForBrand } from "@djsem/analysis-contract";

/**
 * Brand World module — an interactive brand-consistency scorer (prompt-116 weights) that lets
 * users tune colour/typography/mood match sliders and see the resulting brand score live, the
 * same scoring logic every generated asset is checked against.
 *
 * @returns A panel with live sliders and score/badge feedback, plus a short Brand World summary panel.
 */
export function BrandWorldModule() {
  const [color, setColor] = useState(0.8);
  const [typo, setTypo] = useState(0.7);
  const [mood, setMood] = useState(0.9);
  const score = brandConsistencyScore({ primaryColorMatch: color, typographyMatch: typo, moodMatch: mood });
  const needsReview = requiresReviewForBrand(score);
  const slider = (label: string, v: number, set: (n: number) => void) => (
    <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, color: tokens.color.textDim, marginBottom: 8 }}>
      <span style={{ width: 92 }}>{label}</span>
      <input type="range" min={0} max={100} value={Math.round(v * 100)}
        onChange={(e: { target: { value: string } }) => set(Number(e.target.value) / 100)} style={{ flex: 1, accentColor: tokens.color.accent }} />
      <span style={{ width: 34, textAlign: "right", color: tokens.color.text }}>{Math.round(v * 100)}%</span>
    </label>
  );
  return (
    <>
      <PanelShell title="Brand consistency — live scoring">
        {slider("Colour", color, setColor)}
        {slider("Typography", typo, setTypo)}
        {slider("Mood", mood, setMood)}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
          <StatChip label="Brand score" value={`${Math.round(score * 100)}`} tone={needsReview ? "warn" : "ok"} />
          <Badge tone={needsReview ? "warn" : "ok"}>{needsReview ? "needs review before use" : "on-brand"}</Badge>
        </div>
        <div style={{ fontSize: 11, color: tokens.color.muted, marginTop: 10 }}>
          Weights: colour 40% · typography 20% · mood 40% — the same scorer every generated asset runs through.
        </div>
      </PanelShell>
      <PanelShell title="Brand World" style={{ marginTop: 12 }}>
        <p style={{ margin: 0, color: tokens.color.textDim, fontSize: 13 }}>
          Your palette, typography, and mood — applied to every asset and scene DJSEM generates.
        </p>
      </PanelShell>
    </>
  );
}
